<script setup>
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import {
  ref, inject,
} from 'vue';
import apiMaestrosLomloe from '@/api/cifra/lomloe/maestros';
import ModalSimple from '@/components/ModalSimple.vue';
import Spinner from '@/components/Spinner.vue';

const $I18n = inject('I18n');

const showModal = ref(false);
const cursoActual = ref({});
const titulo = ref(null);
const mainForm = ref(null);
const competenciasClave = ref(null);
const cargando = ref(false);
const competenciaSeleccionada = ref(null);
const desciptoresMarcados = ref([]);

const emits = defineEmits(['anadir']);

function byOrden(array) {
  if (isEmpty(array)) { return []; }

  return sortBy(array, (o) => o.orden);
}

async function cargarCompetenciasClave() {
  cargando.value = true;
  try {
    const anadidas = cursoActual.value.competencias_clave.filter(
      (cc) => !cc._destroy,
    ).map(
      (cc) => cc.competencia_clave_definicion.id,
    );
    const infoCursoData = (await apiMaestrosLomloe.infoCurso(cursoActual.value.curso.id)).data;
    competenciasClave.value = byOrden(
      infoCursoData.info_curso.competencias_clave.filter(
        (cc) => !anadidas.includes(cc.id),
      ),
    );
  } finally {
    cargando.value = false;
  }
}

function show(curso, opciones = {}) {
  cursoActual.value = cloneDeep(curso);
  titulo.value = opciones.titulo || $I18n.t('lomloe_competencias_clave_seleccionar');
  showModal.value = true;

  competenciaSeleccionada.value = null;
  desciptoresMarcados.value = [];

  cargarCompetenciasClave();
}

function cerrarModal() {
  showModal.value = false;
}

defineExpose({ show, cerrarModal });

function isDescriptorSeleccionado(descriptor) {
  return desciptoresMarcados.value.some((desc) => desc.id === descriptor.id);
}

function toggleDescriptor(descriptor) {
  if (isDescriptorSeleccionado(descriptor)) {
    desciptoresMarcados.value.splice(
      desciptoresMarcados.value.findIndex((desc) => desc.id === descriptor.id), 1,
    );
  } else {
    desciptoresMarcados.value.push(descriptor);
  }
}

function competenciaClave() {
  const competencia = cloneDeep(cursoActual.value.competencias_clave.find(
    (cc) => cc.competencia_clave_definicion_id === competenciaSeleccionada.value.id,
  ));

  if (competencia) {
    if (competencia._destroy) { delete competencia._destroy; }

    desciptoresMarcados.value.forEach((descriptorMarcado) => {
      const descriptor = competencia.descriptores.find(
        (de) => de.descriptor_operativo_definicion_id === descriptorMarcado.id,
      );
      if (descriptor) {
        if (descriptor._destroy) { delete descriptor._destroy; }
      } else {
        competencia.descriptores.push(
          {
            descriptor_operativo_definicion_id: descriptorMarcado.id,
            descriptor_operativo_definicion: descriptorMarcado,
          },
        );
      }
    });
    return competencia;
  }

  return {
    competencia_clave_definicion_id: competenciaSeleccionada.value.id,
    competencia_clave_definicion: {
      id: competenciaSeleccionada.value.id,
      codigo: competenciaSeleccionada.value.codigo,
      color: competenciaSeleccionada.value.color,
      nombre: competenciaSeleccionada.value.nombre,
      curso_id: cursoActual.value.curso.id,
    },
    descriptores: desciptoresMarcados.value.map((descriptor) => (
      {
        descriptor_operativo_definicion_id: descriptor.id,
        descriptor_operativo_definicion: descriptor,
      }
    )),
  };
}

</script>
<template>
  <ModalSimple
    v-model="showModal"
    :titulo="titulo"
  >
    <div ref="mainForm">
      <Spinner v-if="cargando" :label="$t('cargando_informacion')" />
      <Transition enter-active-class="animated fadeInRight" mode="out-in">
        <div v-if="!cargando" class="competencias-wrapper">
          <div v-if="!isEmpty(competenciasClave)" class="m-t">
            <div class="form-group">
              <select-cifra
                v-model="competenciaSeleccionada"
                :options="competenciasClave"
                :searchable="true"
                :multiple="false"
                label="nombre"
                :placeholder="$I18n.t('lomloe_competencia_clave')"
                track-by="id"
              />
            </div>
            <template v-if="!isEmpty(competenciaSeleccionada)">
              <div v-if="!isEmpty(competenciaSeleccionada.descriptor_operativo_definiciones)">
                <div class="m-t">
                  <h5>{{ $t('lomloe_descriptores_operativos_seleccione') }}</h5>
                </div>
                <ul class="list-group clear-list m-t">
                  <li
                    v-for="descriptor in byOrden(
                      competenciaSeleccionada.descriptor_operativo_definiciones
                    )"
                    :key="descriptor.id"
                    class="list-group-item"
                  >
                    <div class="pretty_checkbox">
                      <div class="pretty p-icon">
                        <input
                          class="pretty_checkbox"
                          type="checkbox"
                          :checked="isDescriptorSeleccionado(descriptor)"
                          @click="toggleDescriptor(descriptor)"
                        >
                        <div class="state p-cifra">
                          <i class="icon fa fa-check"></i>
                          <label class="pretty_checkbox control-label"></label>
                        </div>
                      </div>
                      {{ descriptor.codigo }} - {{ descriptor.nombre }}
                    </div>
                  </li>
                </ul>
              </div>
              <div v-else class="m-t">
                {{ $t('lomloe_descriptores_operativos_no_hay') }}
              </div>
            </template>
          </div>
          <div v-else class="m-t">
            {{ $t('lomloe_competencias_clave_no_hay') }}
          </div>
          <div class="form-actions m-t">
            <button
              class="btn btn-default"
              type="button"
              @click="cerrarModal"
              v-text="$t('Cancelar')"
            />
            <button
              type="button"
              class="btn btn-cifra"
              :disabled="isEmpty(competenciaSeleccionada) || isEmpty(desciptoresMarcados)"
              @click="emits('anadir', competenciaClave())"
            >
              <i class="fa fa-plus"></i> {{ $t('Anadir') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </ModalSimple>
</template>
